import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { startOfDay } from 'date-fns'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { SpaceSummary } from '@/routes/features/home/utils/buildSpaceSummaries'
import { buildSpaceSummaries } from '@/routes/features/home/utils/buildSpaceSummaries'
import { Button } from '@/components/ui/button'
import { PhoneMenuButton } from '@/components/app/layout/PhoneMenuButton'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { QuickCreateTaskDialog } from '@/components/app/QuickCreateTaskDialog'
import { useTaskPageSetup } from '@/hooks'
import { useRootProjects, useTasks } from '@/lib/db/rxdb/hooks'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { cn } from '@/lib/utils'
import { projectAccentColor } from '@/lib/utils/projectIcon'
import { useProjects } from '@/routes/features/tasks/hooks/useProjects'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useTodayTasks } from '@/routes/features/tasks/hooks/useTodayTasks'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import useLoginStore from '@/store/LoginStore'

/**
 * Workspace Home (design 1d): the landing page. "Needs you" lists what is
 * overdue or due today; each space gets a card with its open work and
 * progress. Replaces the empty "all caught up" landing.
 */
export default function HomePage() {
  useTaskPageSetup()
  const { t } = useTranslation('home')
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const workspaceName = useLoginStore(
    (s) => s.workspaces.find((w) => w.workspaceId === s.workspaceId)?.name ?? '',
  )
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { tasks: dueTasks } = useTodayTasks()
  const { tasks: allTasks } = useTasks({ isDeleted: false })
  const { projects } = useProjects()
  const { projects: rootProjects } = useRootProjects()
  const { all: allStatuses, open: openStatuses } = useStatuses()

  const progressTypeByStatusId = useMemo(() => {
    const map = new Map<string, string>()
    for (const status of allStatuses.statuses) map.set(status.id, status.progressType)
    return map
  }, [allStatuses.statuses])

  const projectById = useMemo(() => {
    const map = new Map<string, ProjectDocument>()
    for (const project of projects) map.set(project.id, project)
    return map
  }, [projects])

  const spaces = useMemo(
    () =>
      rootProjects
        .filter((p) => !p.isDeleted && (!workspaceId || p.envId === workspaceId))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [rootProjects, workspaceId],
  )

  const summaries = useMemo(
    () =>
      buildSpaceSummaries({
        spaces,
        projects,
        tasks: allTasks,
        openStatusIds: openStatuses.statusIds,
        startOfToday: startOfDay(new Date()).getTime(),
      }),
    [spaces, projects, allTasks, openStatuses.statusIds],
  )

  const sortedDue = useMemo(
    () => [...dueTasks].sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0)),
    [dueTasks],
  )

  const spaceOf = (task: TaskDocument) => {
    const project = task.projectId ? projectById.get(task.projectId) : undefined
    if (!project) return undefined
    return project.parentProjectId ? projectById.get(project.parentProjectId) : project
  }

  return (
    <div className="mx-auto w-full max-w-[960px] px-6 pb-16 pt-12 md:px-10 phone:px-4 phone:pt-3">
      <header className="mb-8 flex items-end justify-between gap-4 phone:items-start">
        <PhoneMenuButton />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {t('workspaceEyebrow', { name: workspaceName })}
          </p>
          <h1 className="text-[34px] font-bold leading-tight tracking-[-0.02em]">{t('title')}</h1>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5 phone:hidden">
          <Plus className="size-4" />
          {t('newTask')}
        </Button>
      </header>

      <section className="mb-10" aria-labelledby="home-needs-you">
        <h2 id="home-needs-you" className="mb-3 flex items-baseline gap-2">
          <span className="text-[15px] font-semibold">{t('needsYou.title')}</span>
          <span className="text-[13px] text-muted-foreground">{t('needsYou.subtitle')}</span>
        </h2>
        {sortedDue.length === 0 ? (
          <p className="rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground">
            {t('needsYou.empty')}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sortedDue.map((task) => (
              <li key={task.id}>
                <NeedsYouCard
                  task={task}
                  workspaceSlug={workspaceSlug}
                  progressType={progressTypeByStatusId.get(task.statusId) ?? 'BL'}
                  identifier={resolveTaskIdentifier(
                    task,
                    task.projectId ? projectById.get(task.projectId)?.projectKey : null,
                  )}
                  spaceName={spaceOf(task)?.name}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="home-spaces">
        <h2 id="home-spaces" className="mb-3 text-[15px] font-semibold">
          {t('spaces.title')}
        </h2>
        {summaries.length === 0 ? (
          <p className="rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground">
            {t('spaces.empty')}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {summaries.map((summary) => (
              <li key={summary.space.id}>
                <SpaceCard summary={summary} workspaceSlug={workspaceSlug} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <QuickCreateTaskDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </div>
  )
}

function NeedsYouCard({
  task,
  workspaceSlug,
  progressType,
  identifier,
  spaceName,
}: {
  task: TaskDocument
  workspaceSlug: string
  progressType: string
  identifier: string
  spaceName?: string
}) {
  const { t } = useTranslation('home')
  const formatters = useDateTimeFormatters()
  const navigate = useNavigate()
  const isOverdue = task.dueDate != null && task.dueDate < startOfDay(new Date()).getTime()

  return (
    <button
      type="button"
      onClick={() =>
        void navigate({
          to: '/$workspaceSlug/tasks/$taskId',
          params: { workspaceSlug, taskId: task.id },
        })
      }
      className="flex w-full items-center gap-3 rounded-lg border bg-card px-4 py-3 text-left transition-colors hover:border-input"
    >
      <StatusIcon progressType={progressType} statusId={task.statusId} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium">{task.name}</span>
        <span className="block truncate font-mono text-[11px] text-muted-foreground">
          {[identifier, spaceName].filter(Boolean).join(' · ')}
        </span>
      </span>
      {task.dueDate != null && (
        <span
          className={cn(
            'shrink-0 rounded-md px-2 py-0.5 text-[11.5px] font-medium',
            isOverdue
              ? 'bg-brand-tint text-brand-tint-foreground'
              : 'bg-secondary text-secondary-foreground',
          )}
        >
          {isOverdue
            ? formatters.date(new Date(task.dueDate), { month: 'short', day: 'numeric' })
            : t('due.today')}
        </span>
      )}
    </button>
  )
}

function SpaceCard({ summary, workspaceSlug }: { summary: SpaceSummary; workspaceSlug: string }) {
  const { t } = useTranslation('home')
  const { space, projectCount, openCount, overdueCount, progress, previewTasks } = summary
  const color = projectAccentColor(space)

  const meta = [
    t('spaces.projects', { count: projectCount }),
    openCount > 0 ? t('spaces.open', { count: openCount }) : t('spaces.noOpenTasks'),
    overdueCount > 0 ? t('spaces.overdue', { count: overdueCount }) : null,
  ].filter(Boolean)

  return (
    <Link
      to="/$workspaceSlug/spaces/$spaceKey"
      params={{ workspaceSlug, spaceKey: space.projectKey.toLowerCase() }}
      className="group block overflow-hidden rounded-xl border bg-card transition-colors hover:border-input"
    >
      {/* Preview: a tinted well with a paper "sheet" listing open work */}
      <div
        className="relative h-[132px] overflow-hidden"
        style={{ backgroundColor: `color-mix(in oklab, ${color} 16%, var(--card))` }}
      >
        {openCount > 0 && (
          <span className="absolute right-2.5 top-2.5 z-10 flex items-center gap-1.5 rounded-full bg-card px-2 py-0.5 text-[11.5px] font-semibold shadow-xs">
            <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
            {t('spaces.openBadge', { count: openCount })}
          </span>
        )}
        <div
          aria-hidden
          className="absolute left-5 top-8 h-full w-[60%] -rotate-3 rounded-md bg-card/50"
        />
        <div className="absolute left-[18%] top-6 h-full w-[68%] rounded-md bg-card px-3.5 pt-4 shadow-sm">
          <div className="mb-2.5 h-1.5 w-2/5 rounded-full" style={{ backgroundColor: color }} />
          {previewTasks.map((task) => (
            <div
              key={task.id}
              className="mb-1.5 flex items-center gap-1.5 text-[10.5px] text-secondary-foreground"
            >
              <span
                className="size-2 shrink-0 rounded-full border"
                style={{ borderColor: color }}
              />
              <span className="truncate">{task.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="px-4 pb-4 pt-3">
        <p className="truncate text-[15px] font-semibold">{space.name}</p>
        <p className="mb-2.5 truncate text-[13px] text-muted-foreground">{meta.join(' · ')}</p>
        <div className="h-1 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </Link>
  )
}
